import React, { memo, useCallback, type MouseEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { CustomThemeButton } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ModalTriggerButton } from '@atlassian/jira-software-modal-dialog/src/ui/modal-trigger-button/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import type { CardButtonProps } from './types.tsx';

const CardButton = ({
	isActive,
	text,
	isVisible = true,
	analyticName = 'cardButton',
	onClick,
	onFocus,
	onMouseOver,
	isModalTrigger,
}: CardButtonProps) => {
	const onButtonClick = useCallback(
		(event: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			event.stopPropagation();
			fireUIAnalytics(analyticsEvent, analyticName);
			onClick();
		},
		[analyticName, onClick],
	);
	const ButtonComponent = isModalTrigger ? ModalTriggerButton : CustomThemeButton;

	return (
		<ButtonComponent
			testId="software-backlog-panel.common.ui.card-button"
			tabIndex={isVisible ? 0 : -1}
			onClick={onButtonClick}
			shouldFitContainer
			isSelected={isActive}
			onFocus={onFocus}
			onMouseOver={onMouseOver}
			theme={(currentTheme, themeProps) => {
				const { buttonStyles, ...rest } = currentTheme(themeProps);
				return {
					buttonStyles: {
						...buttonStyles,
						...(isVisualRefreshEnabled()
							? {
									color: token('color.text.subtle'),
									borderRadius: token('border.radius.100'),
									borderWidth: token('border.width'),
									borderStyle: 'solid',
									borderColor: token('color.border'),
									backgroundColor: token('color.background.neutral.subtle'),
								}
							: {
									backgroundColor: token('color.background.neutral'),
									color: token('color.text'),
								}),
						margin: `${token('space.050')} ${token('space.0')}`,
						'&:hover': {
							backgroundColor: token('color.background.neutral.hovered'),
						},
						'&:focus': {
							backgroundColor: token('color.background.neutral.pressed'),
						},
						'&:active': {
							backgroundColor: token('color.background.neutral.pressed'),
						},
					},
					...rest,
				};
			}}
		>
			{text}
		</ButtonComponent>
	);
};

export default memo<CardButtonProps>(CardButton);
